import { IAdobeInteraction } from "@abbvie-epi/common/adobe-analytics";
import { Component, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { debounceTime, Subject } from "rxjs";
import { AnalyticsHelperService } from "../../services/analytics-helper.service";
import { QuestionsService } from "../../services/questions.service";
import { ResultsService } from "../../services/results.service";
import { ResultsComponent } from "../results/results.component";

@UntilDestroy()
@Component({
    selector: "app-start-screening",
    templateUrl: "./start-screening.component.html",
    styleUrls: ["./start-screening.component.scss"]
})
export class StartScreeningComponent implements OnInit {
    public hcpModal: boolean = false;
    public videoModal: boolean = false;

    public toolNavType: string = "";
    public progressCode: string = "";

    public selectedValue: string;

    private progressCodeModelChanged: Subject<string> = new Subject<string>();
    private progressCodeInitialInteraction: boolean = true;

    @ViewChild(ResultsComponent) results: ResultsComponent;

    public hasResults: boolean;
    public questionIdList: any;

    constructor(
        private resultsService: ResultsService,
        private questionsService: QuestionsService,
        private router: Router,
        private analyticsHelper: AnalyticsHelperService
    ) {}

    async ngOnInit(): Promise<void> {
        this.getResultStatus();

        this.questionIdList = await this.questionsService.getQuestionIdList();

        this.progressCodeModelChanged.pipe(untilDestroyed(this), debounceTime(500)).subscribe((value) => {
            const analyticsOverride: IAdobeInteraction = {
                link: {
                    name: `Progress code: ${value}`,
                    title: `Progress code: ${value}`
                },
                component: {
                    name: "form",
                    title: "Continuing an existing screening",
                    position: "content",
                    type: "form"
                },
                form: {
                    name: "Continuing an existing screening",
                    category: "Services",
                    subCategory: "Symptom Checker",
                    fieldName: "Progress code",
                    fieldValue: value
                },
                content: {
                    type: "form"
                },
                interaction: {
                    type: "form interaction"
                }
            };

            if (this.progressCodeInitialInteraction) {
                this.progressCodeInitialInteraction = false;

                analyticsOverride.link!.type = "form-initiation";
                analyticsOverride.form!.stepName = "initiation";
            } else {
                analyticsOverride.link!.type = "form-interaction";
                analyticsOverride.form!.stepName = "interaction";
            }

            this.analyticsHelper.trackCustom(analyticsOverride);
        });
    }

    private getResultStatus() {
        if (this.resultsService.getResultStatus()) {
            this.hasResults = true;
        } else {
            this.hasResults = false;
        }
    }

    // Video modal
    public hideVideoModal() {
        this.videoModal = false;
        this.analyticsHelper.trackInteraction("screening-tool-landing-start-screening-user-guide-modal-close");
    }

    public openModal(trackingId?: string) {
        this.videoModal = true;

        if (trackingId) {
            this.analyticsHelper.trackInteraction(trackingId);
        }
    }

    // Start or Continue screening
    public start() {
        if (sessionStorage.getItem("hcpSpecialty")) {
            this.router.navigate(["screening-tool"]);
        } else {
            this.hcpModal = true;
            this.toolNavType = "start";
        }

        this.analyticsHelper.trackInteraction("screening-tool-landing-start-screening-start-screening-cta");
    }

    public async isProgressCodeValid() {
        const questionIdList: string[] = await this.questionsService.getQuestionIdList();

        if (questionIdList.includes(this.progressCode)) {
            return true;
        }
        return false;
    }

    public async continue() {
        if (this.progressCode && (await this.isProgressCodeValid()) === true) {
            if (sessionStorage.getItem("hcpSpecialty")) {
                this.router.navigate(["screening-tool"], { fragment: this.progressCode });
            } else {
                this.hcpModal = true;
            }

            const analyticsOverride: IAdobeInteraction = {
                form: {
                    fieldValues: this.progressCode
                }
            };

            this.analyticsHelper.trackInteraction(
                "screening-tool-landing-start-screening-continue-screening-submit",
                analyticsOverride
            );
        }
    }

    public continueNavigation() {
        if (this.toolNavType === "start") {
            this.start();
        } else if (this.toolNavType === "continue") {
            this.continue();
        } else {
            throw Error("Invalid navigation type");
        }
    }

    public clearResults() {
        this.hasResults = false;
        window.scroll(0, 0);
    }

    // Is HCP?
    public handleModalClose() {
        this.hcpModal = false;
    }

    public notifyProgressCodeChanged(value: string): void {
        this.progressCodeModelChanged.next(value);
    }
}
